// Super basic stick figure avatar generator
// Circle head, line body, line arms, line legs — napkin doodle vibes
// Accessories: monocle, top hat, money bag, dollar bills, briefcase, etc.

export default function generateAvatar(username) {
  const name = username || 'anon';

  const hash = (str, seed = 0) => {
    let h = seed;
    for (let i = 0; i < str.length; i++) { h = Math.imul(31, h) + str.charCodeAt(i) | 0; }
    return Math.abs(h);
  };

  const h = [0,1,2,3,4,5,6,7,8,9].map(s => hash(name, s));
  const pick = (arr, idx) => arr[h[idx % 10] % arr.length];

  const bg = pick(['#1a1a2e','#16213e','#0f3460','#2c2c54','#1e272e','#192a56','#2d3436','#1b1b2f','#0d1117','#1a1a3e'], 0);
  const stroke = '#e0e0e0';
  const sw = '3'; // stroke width

  // --- Face variations ---
  const faceIdx = h[1] % 6;
  let face = '';
  switch (faceIdx) {
    case 0: // happy
      face = `<circle cx="88" cy="58" r="3" fill="${stroke}"/><circle cx="112" cy="58" r="3" fill="${stroke}"/>
              <path d="M88,70 Q100,82 112,70" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/>`;
      break;
    case 1: // big grin
      face = `<circle cx="88" cy="58" r="3" fill="${stroke}"/><circle cx="112" cy="58" r="3" fill="${stroke}"/>
              <path d="M85,68 Q100,85 115,68" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/>`;
      break;
    case 2: // smirk
      face = `<circle cx="88" cy="58" r="3" fill="${stroke}"/><circle cx="112" cy="58" r="3" fill="${stroke}"/>
              <path d="M90,72 Q102,72 114,66" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/>`;
      break;
    case 3: // neutral
      face = `<circle cx="88" cy="58" r="3" fill="${stroke}"/><circle cx="112" cy="58" r="3" fill="${stroke}"/>
              <line x1="90" y1="72" x2="110" y2="72" stroke="${stroke}" stroke-width="2.5" stroke-linecap="round"/>`;
      break;
    case 4: // wink + smile
      face = `<circle cx="88" cy="58" r="3" fill="${stroke}"/>
              <path d="M108,56 Q112,60 116,56" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/>
              <path d="M88,70 Q100,82 112,70" stroke="${stroke}" stroke-width="2.5" fill="none" stroke-linecap="round"/>`;
      break;
    case 5: // surprised
      face = `<circle cx="88" cy="58" r="3" fill="${stroke}"/><circle cx="112" cy="58" r="3" fill="${stroke}"/>
              <ellipse cx="100" cy="74" rx="5" ry="6" stroke="${stroke}" stroke-width="2.5" fill="none"/>`;
      break;
  }

  // --- Hat / head accessory ---
  const hatIdx = h[2] % 7;
  let hat = '';
  switch (hatIdx) {
    case 0: // top hat
      hat = `<rect x="82" y="18" width="36" height="24" rx="2" stroke="${stroke}" stroke-width="${sw}" fill="none"/>
             <line x1="74" y1="42" x2="126" y2="42" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      break;
    case 1: // bowler hat
      hat = `<path d="M78,44 Q78,22 100,18 Q122,22 122,44" stroke="${stroke}" stroke-width="${sw}" fill="none"/>
             <line x1="72" y1="44" x2="128" y2="44" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      break;
    case 2: // crown
      hat = `<polyline points="80,44 80,28 90,36 100,24 110,36 120,28 120,44" stroke="${stroke}" stroke-width="${sw}" fill="none" stroke-linejoin="round"/>`;
      break;
    case 3: // no hat
      break;
    case 4: // no hat
      break;
    case 5: // party hat
      hat = `<polygon points="100,16 82,44 118,44" stroke="${stroke}" stroke-width="${sw}" fill="none" stroke-linejoin="round"/>
             <circle cx="100" cy="16" r="3" fill="${stroke}"/>`;
      break;
    case 6: // headband dollar
      hat = `<line x1="72" y1="44" x2="128" y2="44" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
             <text x="93" y="40" font-size="16" font-weight="bold" fill="${stroke}" font-family="sans-serif">$</text>`;
      break;
  }

  // --- Eye accessory ---
  const eyeAccIdx = h[3] % 5;
  let eyeAcc = '';
  switch (eyeAccIdx) {
    case 0: // monocle on right eye
      eyeAcc = `<circle cx="112" cy="58" r="9" stroke="${stroke}" stroke-width="2" fill="none"/>
                <line x1="121" y1="58" x2="128" y2="78" stroke="${stroke}" stroke-width="1.5"/>`;
      break;
    case 1: // round glasses
      eyeAcc = `<circle cx="88" cy="58" r="9" stroke="${stroke}" stroke-width="1.5" fill="none"/>
                <circle cx="112" cy="58" r="9" stroke="${stroke}" stroke-width="1.5" fill="none"/>
                <line x1="97" y1="58" x2="103" y2="58" stroke="${stroke}" stroke-width="1.5"/>`;
      break;
    case 2: // no eye accessory
      break;
    case 3: // no eye accessory
      break;
    case 4: // sunglasses
      eyeAcc = `<rect x="79" y="52" width="18" height="12" rx="2" stroke="${stroke}" stroke-width="1.5" fill="none"/>
                <rect x="103" y="52" width="18" height="12" rx="2" stroke="${stroke}" stroke-width="1.5" fill="none"/>
                <line x1="97" y1="58" x2="103" y2="58" stroke="${stroke}" stroke-width="1.5"/>`;
      break;
  }

  // --- Arm pose + held item ---
  // Left arm always waves or hangs. Right arm holds an item or hangs.
  const armIdx = h[4] % 8;
  let arms = '';
  let heldItem = '';

  // Body: neck at (100,85) down to (100,140)
  // Arms come from ~(100,100)
  switch (armIdx) {
    case 0: // holding money bag (right hand)
      arms = `<line x1="100" y1="100" x2="60" y2="120" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="100" x2="140" y2="130" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      // money bag
      heldItem = `<circle cx="148" cy="126" r="14" stroke="${stroke}" stroke-width="2.5" fill="none"/>
                  <text x="142" y="132" font-size="16" font-weight="bold" fill="${stroke}" font-family="sans-serif">$</text>`;
      break;
    case 1: // holding dollar bill (right hand up)
      arms = `<line x1="100" y1="100" x2="60" y2="130" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="100" x2="145" y2="95" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      heldItem = `<rect x="146" y="84" width="28" height="16" rx="2" stroke="${stroke}" stroke-width="2" fill="none"/>
                  <text x="154" y="97" font-size="12" font-weight="bold" fill="${stroke}" font-family="sans-serif">$</text>`;
      break;
    case 2: // briefcase (right hand down)
      arms = `<line x1="100" y1="100" x2="60" y2="120" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="100" x2="140" y2="140" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      heldItem = `<rect x="134" y="140" width="22" height="16" rx="2" stroke="${stroke}" stroke-width="2.5" fill="none"/>
                  <path d="M141,140 L141,136 Q141,133 145,133 Q149,133 149,136 L149,140" stroke="${stroke}" stroke-width="2" fill="none"/>`;
      break;
    case 3: // waving (left arm up)
      arms = `<line x1="100" y1="100" x2="55" y2="80" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="100" x2="140" y2="130" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      // little hand wave lines
      heldItem = `<line x1="48" y1="72" x2="44" y2="66" stroke="${stroke}" stroke-width="2" stroke-linecap="round"/>
                  <line x1="55" y1="74" x2="53" y2="66" stroke="${stroke}" stroke-width="2" stroke-linecap="round"/>`;
      break;
    case 4: // holding coin (right hand out)
      arms = `<line x1="100" y1="100" x2="60" y2="130" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="100" x2="148" y2="108" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      heldItem = `<circle cx="156" cy="108" r="10" stroke="${stroke}" stroke-width="2.5" fill="none"/>
                  <text x="151" y="113" font-size="12" font-weight="bold" fill="${stroke}" font-family="sans-serif">$</text>`;
      break;
    case 5: // arms down relaxed
      arms = `<line x1="100" y1="100" x2="65" y2="138" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="100" x2="135" y2="138" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      break;
    case 6: // holding chart (right hand)
      arms = `<line x1="100" y1="100" x2="60" y2="125" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="100" x2="145" y2="100" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      heldItem = `<rect x="146" y="86" width="28" height="22" rx="1" stroke="${stroke}" stroke-width="2" fill="none"/>
                  <polyline points="150,104 156,96 162,100 170,90" stroke="${stroke}" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
      break;
    case 7: // hands on hips
      arms = `<polyline points="65,120 60,108 100,100" stroke="${stroke}" stroke-width="${sw}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="135,120 140,108 100,100" stroke="${stroke}" stroke-width="${sw}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`;
      break;
  }

  // --- Legs ---
  const legIdx = h[5] % 3;
  let legs = '';
  switch (legIdx) {
    case 0: // standing
      legs = `<line x1="100" y1="140" x2="75" y2="185" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="140" x2="125" y2="185" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      break;
    case 1: // wider stance
      legs = `<line x1="100" y1="140" x2="68" y2="185" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="140" x2="132" y2="185" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      break;
    case 2: // walking
      legs = `<line x1="100" y1="140" x2="70" y2="182" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="100" y1="140" x2="130" y2="182" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="70" y1="182" x2="62" y2="182" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
              <line x1="130" y1="182" x2="138" y2="182" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`;
      break;
  }

  // --- Extra flair (small floating element) ---
  const flairIdx = h[6] % 6;
  let flair = '';
  switch (flairIdx) {
    case 0: flair = `<text x="20" y="30" font-size="18" fill="${stroke}" opacity="0.3" font-family="sans-serif">$</text>`; break;
    case 1: flair = `<text x="16" y="28" font-size="14" fill="${stroke}" opacity="0.25" font-family="sans-serif">$</text>
                     <text x="28" y="42" font-size="11" fill="${stroke}" opacity="0.2" font-family="sans-serif">$</text>`; break;
    case 2: flair = `<text x="14" y="34" font-size="20" fill="${stroke}" opacity="0.25" font-family="sans-serif">%</text>`; break;
    default: break; // no flair
  }

  // --- Tie or bowtie ---
  const tieIdx = h[7] % 4;
  let tieSvg = '';
  switch (tieIdx) {
    case 0: // simple tie
      tieSvg = `<line x1="100" y1="86" x2="100" y2="108" stroke="${stroke}" stroke-width="2.5" stroke-linecap="round"/>`;
      break;
    case 1: // bowtie
      tieSvg = `<polygon points="100,90 90,86 90,96" stroke="${stroke}" stroke-width="1.5" fill="none"/>
                <polygon points="100,90 110,86 110,96" stroke="${stroke}" stroke-width="1.5" fill="none"/>`;
      break;
    case 2: // no tie
      break;
    case 3: // long tie with knot
      tieSvg = `<polygon points="100,88 96,108 100,105 104,108" stroke="${stroke}" stroke-width="1.5" fill="none"/>`;
      break;
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
  <rect width="200" height="200" fill="${bg}"/>
  ${flair}
  <circle cx="100" cy="60" r="22" stroke="${stroke}" stroke-width="${sw}" fill="none"/>
  ${face}
  ${eyeAcc}
  ${hat}
  <line x1="100" y1="82" x2="100" y2="140" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>
  ${tieSvg}
  ${arms}
  ${heldItem}
  ${legs}
</svg>`;

  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}
